import { computed, Injectable, inject, Injector } from '@angular/core';
import { ProneService } from '@app/prone-operations/services/prone.service';

export type RuntimeMode = 'web' | 'kiosk';
export type RuntimeModeConfidence = 'confirmed';

interface StoredUserRecord {
  company_id?: number | string;
  companyId?: number | string;
  worker_id?: number | string;
  workerId?: number | string;
  user_id?: number | string;
  userId?: number | string;
  id?: number | string;
  [key: string]: unknown;
}

export interface RuntimeContext {
  mode: RuntimeMode;
  confidence: RuntimeModeConfidence;
}

export interface SessionUserContext {
  raw: StoredUserRecord | undefined;
  companyId: number | undefined;
  workerId: number | undefined;
}

export interface ApiSessionContext {
  device: RuntimeMode;
  companyId: number | undefined;
  workerId: number | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class SessionContextService {
  private readonly injector = inject(Injector);

  private get proneService(): ProneService {
    return this.injector.get(ProneService);
  }

  public readonly hasKioskToken = computed<boolean>(() => !!this.proneService.proneToken());

  public readonly runtimeContext = computed<RuntimeContext>(() => {
    if (this.hasKioskToken()) {
      return { mode: 'kiosk', confidence: 'confirmed' };
    }

    return { mode: 'web', confidence: 'confirmed' };
  });

  public readonly mode = computed<RuntimeMode>(() => {
    return this.runtimeContext().mode;
  });

  public readonly modeConfidence = computed<RuntimeModeConfidence>(() => {
    return this.runtimeContext().confidence;
  });

  public readonly userContext = computed<SessionUserContext>(() => {
    const raw = this.getStoredUserRecord();

    return {
      raw,
      companyId: this.readNumber(raw?.company_id ?? raw?.companyId),
      workerId: this.readNumber(raw?.worker_id ?? raw?.workerId ?? raw?.user_id ?? raw?.userId ?? raw?.id),
    };
  });

  public readonly workerDni = computed<string | undefined>(() => {
    const raw = this.getStoredUserRecord();
    return typeof raw?.['dni'] === 'string' ? raw['dni'] : undefined;
  });

  public readonly workerName = computed<string | undefined>(() => {
    const raw = this.getStoredUserRecord();
    return typeof raw?.['name'] === 'string' ? raw['name'] : undefined;
  });


  public readonly apiContext = computed<ApiSessionContext>(() => {
    return {
      device: this.mode(),
      companyId: this.getCompanyId(),
      workerId: this.getWorkerId(),
    };
  });

  public isKioskConfirmed(): boolean {
    return this.mode() === 'kiosk';
  }

  public getCompanyId(_mode: RuntimeMode = this.mode()): number | undefined {
    const raw = this.getStoredUserRecord();
    return this.readNumber(raw?.company_id ?? raw?.companyId);
  }

  public requireCompanyId(operation: string): number {
    const companyId = this.getCompanyId();
    if (companyId === undefined || companyId === null) {
      throw new Error(`[SessionContextService] Missing companyId for ${operation}`);
    }

    return companyId;
  }

  public getWorkerId(): number | undefined {
    const raw = this.getStoredUserRecord();
    return this.readNumber(raw?.worker_id ?? raw?.workerId ?? raw?.user_id ?? raw?.userId ?? raw?.id);
  }

  public requireWorkerId(operation: string): number {
    const workerId = this.getWorkerId();
    if (workerId === undefined || workerId === null) {
      throw new Error(`[SessionContextService] Missing workerId for ${operation}`);
    }

    return workerId;
  }

  private getStoredUserRecord(): StoredUserRecord | undefined {
    const rawValue = localStorage.getItem('user');
    if (!rawValue) {
      return undefined;
    }

    try {
      return JSON.parse(rawValue) as StoredUserRecord;
    } catch {
      return undefined;
    }

  }

  private readNumber(value: unknown): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return undefined;
    }

    return parsedValue;
  }
}
